import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/**
 * Il tastierino del PIN: quattro pallini, dodici tasti, una frase d'errore.
 *
 * ## Perché è un componente e non due copie
 *
 * Perché lo stesso tastierino serve in due posti — la pagina del controllo genitori e la richiesta
 * prima di riprodurre — e sono lo stesso gesto. Due copie divergono alla prima correzione, e la
 * seconda si scopre da uno screenshot.
 *
 * ## I pallini si riempiono, le cifre non si vedono
 *
 * Su un televisore chi guarda non è solo in stanza. È anche il motivo per cui i tasti non si
 * illuminano in modo diverso premendoli: un tastierino che lampeggia si legge dall'altra parte del
 * divano.
 *
 * ## `⌫` e `Annulla` sono tasti, non gesti
 *
 * Su un telecomando non c'è un backspace, e affidarsi al solo tasto Indietro vorrebbe dire che
 * cancellare una cifra sbagliata e rinunciare sono la stessa pressione — con esiti molto diversi.
 */

export interface PinPadProps {
  readonly focusPort: FocusPort;
  /** Quante cifre sono state digitate: riempie i pallini. */
  readonly entered: number;
  /** Quanti pallini. Quattro, da `PIN_LENGTH`. */
  readonly length: number;
  /** La frase d'errore già tradotta, o `null`. */
  readonly error: string | null;
  readonly onDigit: (digit: string) => void;
  readonly onErase: () => void;
  readonly onCancel: () => void;
}

/** La griglia del disegno: 1–9, poi `⌫ 0 Annulla`. */
const KEYS: ReadonlyArray<string> = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'erase', '0', 'cancel'];

const COLUMNS = 3;

export function PinPad({
  focusPort,
  entered,
  length,
  error,
  onDigit,
  onErase,
  onCancel,
}: PinPadProps): ReactNode {
  const translator = useTranslator();
  // Parte dall'1: è il tasto che si preme per primo nella stragrande maggioranza dei casi.
  const [cursor, setCursor] = useState(0);

  const handlers = useRef({ onDigit, onErase, onCancel });
  useEffect(() => {
    handlers.current = { onDigit, onErase, onCancel };
  }, [onDigit, onErase, onCancel]);

  const press = useCallback((key: string) => {
    const api = handlers.current;
    if (key === 'erase') api.onErase();
    else if (key === 'cancel') api.onCancel();
    else api.onDigit(key);
  }, []);

  useEffect(() => {
    return focusPort.subscribe((key) => {
      /*
        **Anche le cifre vere del telecomando.**

        Ogni telecomando ha i numeri, e su una richiesta di PIN sono la prima cosa che si preme —
        usarli è più veloce che portare un cursore su una griglia. Senza questa riga i tasti
        numerici non farebbero niente, che è indistinguibile da un telecomando con le pile scariche.
      */
      if (key.kind === 'character' && /^[0-9]$/.test(key.character)) {
        handlers.current.onDigit(key.character);
        return true;
      }
      if (key.kind === 'erase') {
        handlers.current.onErase();
        return true;
      }
      if (key.kind === 'select') {
        const value = KEYS[cursor];
        if (value !== undefined) press(value);
        return true;
      }
      if (key.kind !== 'direction') return false;

      // Ai bordi si resta: una griglia che avvolge fa premere due volte per tornare dove si era, e
      // su un tastierino di dodici tasti si sente.
      if (key.direction === 'left' && cursor % COLUMNS > 0) setCursor(cursor - 1);
      if (key.direction === 'right' && cursor % COLUMNS < COLUMNS - 1) setCursor(cursor + 1);
      if (key.direction === 'up' && cursor >= COLUMNS) setCursor(cursor - COLUMNS);
      if (key.direction === 'down' && cursor + COLUMNS < KEYS.length) setCursor(cursor + COLUMNS);
      return true;
    });
  }, [focusPort, cursor, press]);

  return (
    <div data-pin-pad="1" style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', gap: 18, justifyContent: 'center' }}>
        {Array.from({ length }, (_, index) => (
          <span
            key={index}
            data-pin-dot={index < entered ? 'full' : 'empty'}
            style={{
              width: 26,
              height: 26,
              borderRadius: 13,
              background: index < entered ? colors.accent : 'transparent',
              border: `3px solid ${index < entered ? colors.accent : colors.border}`,
              boxSizing: 'border-box',
            }}
          />
        ))}
      </div>

      {/*
        L'errore sta **sopra** il tastierino e non sotto: sotto finisce fuori dallo schermo su un
        televisore con overscan, cioè proprio dove non lo leggerebbe nessuno.
      */}
      {error !== null && (
        <span
          data-pin-error="1"
          role="alert"
          style={{ font: `600 22px ${FONT_STACK}`, color: colors.errorText, textAlign: 'center' }}
        >
          {error}
        </span>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLUMNS}, 120px)`, gap: 14 }}>
        {KEYS.map((key, index) => {
          const focused = cursor === index;
          const wide = key === 'cancel' || key === 'erase';
          return (
            <button
              key={key}
              type="button"
              data-f="1"
              data-pin-key={key}
              data-focused={focused ? '1' : '0'}
              onClick={() => press(key)}
              style={{
                height: 96,
                border: 0,
                borderRadius: 14,
                cursor: 'pointer',
                background: colors.surfaceRaised,
                color: colors.text,
                font: `${wide ? '600 22px' : '700 38px'} ${FONT_STACK}`,
                outline: focused ? `4px solid ${colors.accent}` : 'none',
                outlineOffset: 4,
                transform: focused ? 'scale(var(--fs, 1.04))' : 'none',
                transition: 'var(--tr, transform .16s ease)',
              }}
            >
              {key === 'erase' ? '⌫' : key === 'cancel' ? translator.t('pin.cancel') : key}
            </button>
          );
        })}
      </div>
    </div>
  );
}
